import { useState } from 'react'
import type { FormEvent } from 'react'
import { Button, Description, Label, NumberField, Popover } from '@heroui/react'
import { useController } from 'react-hook-form'
import type { Control } from 'react-hook-form'

import { ChevronDownIcon, MoneyIcon } from '@/components/ui/icons'

import { marginLabel } from '../product-form.mapper'
import type { ProductFormValues } from '../schemas'

import { COST_HELP, COST_STEP, INCENTIVE_HELP, centsFormat, costFormat } from './price-format'

interface VariantPriceFieldProps {
  control: Control<ProductFormValues>
  /** The variant's position, which is also how the field is named for a screen reader. */
  index: number
  isDisabled?: boolean
}

/** The three figures the popover edits together. Blank is `undefined`, never zero. */
interface PriceDraft {
  price?: number
  cost?: number
  incentive?: number
}

/** `12.50`, or `not set` — how a figure reads in the trigger's accessible name. */
function spoken(value: number | undefined, digits: number): string {
  return value === undefined || Number.isNaN(value) ? 'not set' : value.toFixed(digits)
}

/**
 * A variant's price, cost and incentive, edited in a popover rather than inside a
 * cell.
 *
 * The cell is only a button showing the price: a number field squeezed into a grid
 * column has no room for its steppers, its currency symbol or its own error, and
 * the popover has all three. Cost and incentive ride in the same popover rather
 * than taking two more columns — they are read against the price, and the grid is
 * already scrolling sideways on a narrow screen. The cell owns up to a cost by
 * showing the margin under the figure once there is one.
 *
 * What is typed is held as a draft and written to the form on Done, so Cancel — or
 * clicking away — leaves all three as they were.
 *
 * The editor is a real `<form>` so Enter does what it does everywhere else: the
 * number field commits what is typed, then the submit lands on Done. Its submit is
 * stopped here — React carries synthetic events up through a portal, so without
 * that the product dialog's own form would be submitted too.
 */
export function VariantPriceField({ control, index, isDisabled }: VariantPriceFieldProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState<PriceDraft>({})

  // The ref is taken apart from the rest: it goes on the trigger, so a failed save
  // can focus the cell, and the hooks lint reads an object holding a ref as off
  // limits during render — the figure has to come from somewhere else.
  const {
    field: { ref: priceRef, ...price },
    fieldState: priceState,
  } = useController({
    control,
    name: `variants.${index}.price`,
  })
  const { field: cost, fieldState: costState } = useController({
    control,
    name: `variants.${index}.cost`,
  })
  const { field: incentive, fieldState: incentiveState } = useController({
    control,
    name: `variants.${index}.incentive`,
  })

  const label = `Price, variant ${index + 1}`
  const error = priceState.error ?? costState.error ?? incentiveState.error
  const margin = marginLabel(price.value, cost.value)
  const draftMargin = marginLabel(draft.price, draft.cost)

  function handleOpenChange(next: boolean) {
    // The trigger is a styled div rather than a button, so it has no disabled
    // state of its own to stop the press.
    if (next && isDisabled) return

    // Seeded on the way in, so a draft abandoned last time never comes back.
    if (next) setDraft({ price: price.value, cost: cost.value, incentive: incentive.value })
    setIsOpen(next)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    event.stopPropagation()

    price.onChange(draft.price)
    cost.onChange(draft.cost)
    incentive.onChange(draft.incentive)
    price.onBlur()
    cost.onBlur()
    incentive.onBlur()
    setIsOpen(false)
  }

  /** React Aria reports an emptied field as `NaN`; the form holds that as blank. */
  function setFigure(key: keyof PriceDraft) {
    return (value: number) =>
      setDraft((current) => ({ ...current, [key]: Number.isNaN(value) ? undefined : value }))
  }

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <Popover isOpen={isOpen} onOpenChange={handleOpenChange}>
        {/* Wears the `input` class the SKU and Barcode boxes are built from, so
            the height, border, radius, hover, focus and invalid states all
            come from the same place rather than being copied beside it. */}
        <Popover.Trigger
          ref={priceRef}
          aria-label={`${label}: ${spoken(price.value, 2)}, cost ${spoken(
            cost.value,
            4,
          )}, incentive ${spoken(incentive.value, 2)}. Edit`}
          aria-disabled={isDisabled || undefined}
          data-disabled={isDisabled || undefined}
          data-invalid={Boolean(error) || undefined}
          tabIndex={isDisabled ? -1 : 0}
          className="input flex w-full min-w-0 cursor-pointer items-center gap-2 tabular-nums"
        >
          {/* Names the unit, so the figure needs no `$` of its own. */}
          <MoneyIcon width={14} height={14} aria-hidden="true" className="text-muted shrink-0" />
          <span className="min-w-0 flex-1 truncate">
            {price.value === undefined || Number.isNaN(price.value) ? (
              <span className="text-field-placeholder">0.00</span>
            ) : (
              price.value.toFixed(2)
            )}
          </span>
          {/* Says the cell opens something, which a bare figure does not. The
              chevron turns over while the popover is up. */}
          <ChevronDownIcon
            width={14}
            height={14}
            aria-hidden="true"
            className={`text-muted shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </Popover.Trigger>

        {/* Portaled to `document.body`, outside the shell — so the brand token
            scope has to travel with the panel. */}
        <Popover.Content placement="bottom end" offset={8} className="overlay-scope">
          <Popover.Dialog className="w-[min(18rem,calc(100vw-2rem))]">
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <Popover.Heading className="text-sm font-semibold tracking-tight">
                Variant {index + 1} pricing
              </Popover.Heading>

              <NumberField
                value={draft.price ?? Number.NaN}
                onChange={setFigure('price')}
                minValue={0}
                step={0.01}
                formatOptions={centsFormat}
                autoFocus
                fullWidth
                className="flex flex-col gap-1.5"
              >
                <Label>Price</Label>
                <NumberField.Group className="w-full">
                  <NumberField.DecrementButton className="shrink-0" />
                  <NumberField.Input
                    onFocus={(event) => event.currentTarget.select()}
                    inputMode="decimal"
                    className="min-w-0 flex-1 text-center"
                  />
                  <NumberField.IncrementButton className="shrink-0" />
                </NumberField.Group>
                {draftMargin && <Description>{draftMargin}</Description>}
              </NumberField>

              <NumberField
                value={draft.cost ?? Number.NaN}
                onChange={setFigure('cost')}
                minValue={0}
                step={COST_STEP}
                formatOptions={costFormat}
                fullWidth
                className="flex flex-col gap-1.5"
              >
                <Label>Cost</Label>
                <NumberField.Group className="w-full">
                  <NumberField.DecrementButton className="shrink-0" />
                  <NumberField.Input
                    placeholder="Not set"
                    onFocus={(event) => event.currentTarget.select()}
                    inputMode="decimal"
                    className="min-w-0 flex-1 text-center"
                  />
                  <NumberField.IncrementButton className="shrink-0" />
                </NumberField.Group>
                <Description>{COST_HELP}</Description>
              </NumberField>

              <NumberField
                value={draft.incentive ?? Number.NaN}
                onChange={setFigure('incentive')}
                minValue={0}
                step={0.01}
                formatOptions={centsFormat}
                fullWidth
                className="flex flex-col gap-1.5"
              >
                <Label>Incentive</Label>
                <NumberField.Group className="w-full">
                  <NumberField.DecrementButton className="shrink-0" />
                  <NumberField.Input
                    placeholder="Not set"
                    onFocus={(event) => event.currentTarget.select()}
                    inputMode="decimal"
                    className="min-w-0 flex-1 text-center"
                  />
                  <NumberField.IncrementButton className="shrink-0" />
                </NumberField.Group>
                <Description>{INCENTIVE_HELP}</Description>
              </NumberField>

              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onPress={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" size="sm">
                  Done
                </Button>
              </div>
            </form>
          </Popover.Dialog>
        </Popover.Content>
      </Popover>

      {error ? (
        <p role="alert" className="text-danger text-[11px] leading-snug">
          {error.message}
        </p>
      ) : (
        margin && <p className="text-muted text-[11px] leading-snug tabular-nums">{margin}</p>
      )}
    </div>
  )
}
